<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'

const props = defineProps({
  leagueId: {
    type: [String, Number],
    required: true
  },
  teamId: {
    type: [String, Number],
    required: true
  }
})

// Component state
const loading = ref(false)
const error = ref(null)
const projectionWeights = ref(null)

// Fetch player label projection weights on mount
const fetchProjectionWeights = async () => {
  if (!props.leagueId || !props.teamId) {
    error.value = 'League ID and Team ID are required'
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await axios.post('api/v2/projections-settings/player-labels/projection-weights', {
      league_id: props.leagueId,
      team_id: props.teamId
    })
    
    projectionWeights.value = response.data
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Failed to fetch projection weights'
    console.error('Error fetching projection weights:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchProjectionWeights()
})
</script>

<template>
  <div class="space-y-4">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="flex items-center gap-3">
        <i class="fas fa-spinner fa-spin text-blue-500"></i>
        <span class="text-gray-600">Loading projection weights...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center gap-3">
        <i class="fas fa-exclamation-triangle text-red-500"></i>
        <div>
          <h4 class="text-red-800 font-medium">Error Loading Projection Weights</h4>
          <p class="text-red-600 text-sm mt-1">{{ error }}</p>
        </div>
      </div>
      <button 
        @click="fetchProjectionWeights" 
        class="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
      >
        <i class="fas fa-redo mr-2"></i>
        Retry
      </button>
    </div>

    <!-- Success State -->
    <div v-else-if="projectionWeights" class="space-y-4">
      <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div class="flex items-center gap-3">
          <i class="fas fa-weight text-purple-500"></i>
          <div>
            <h4 class="text-purple-800 font-medium">Projection Weights Loaded</h4>
            <p class="text-purple-600 text-sm mt-1">
              Found {{ Array.isArray(projectionWeights) ? projectionWeights.length : Object.keys(projectionWeights).length }} projection weight entries
            </p>
          </div>
        </div>
      </div>

      <!-- Projection Weights Display -->
      <div class="bg-white rounded-lg border overflow-hidden">
        <div class="p-4 border-b bg-gray-50">
          <h5 class="font-medium text-gray-800">Player Label Projection Weights</h5>
        </div>
        
        <!-- If it's an array -->
        <div v-if="Array.isArray(projectionWeights)" class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="(weight, index) in projectionWeights" :key="index" 
                 class="bg-gray-50 rounded-lg p-3 border">
              <div class="text-sm font-medium text-gray-900 mb-2">Weight {{ index + 1 }}</div>
              <div class="text-xs text-gray-600">
                <pre class="whitespace-pre-wrap">{{ JSON.stringify(weight, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
        
        <!-- If it's an object -->
        <div v-else class="p-4">
          <div class="space-y-3">
            <div v-for="(value, key) in projectionWeights" :key="key" 
                 class="bg-gray-50 rounded-lg p-3 border">
              <div class="text-sm font-medium text-gray-900 mb-2">{{ key }}</div>
              <div class="text-xs text-gray-600">
                <pre class="whitespace-pre-wrap">{{ JSON.stringify(value, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Information -->
      <div class="bg-gray-50 rounded-lg p-4">
        <h5 class="font-medium text-gray-800 mb-3">Weight Summary</h5>
        <div class="text-sm text-gray-600">
          <p><span class="font-medium">Data Type:</span> {{ Array.isArray(projectionWeights) ? 'Array' : 'Object' }}</p>
          <p><span class="font-medium">Total Items:</span> {{ Array.isArray(projectionWeights) ? projectionWeights.length : Object.keys(projectionWeights).length }}</p>
          <p><span class="font-medium">League ID:</span> {{ leagueId }}</p>
          <p><span class="font-medium">Team ID:</span> {{ teamId }}</p>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-8">
      <i class="fas fa-info-circle text-gray-400 text-4xl mb-4"></i>
      <p class="text-gray-600">No projection weights data available</p>
    </div>
  </div>
</template>

<style scoped>
/* Additional custom styles if needed */
</style>



